import React, { FC, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSignupWizard } from './signup-wizard-context';
import WizardLayout from './wizard-layout';
import StepHeader from './step-header';
import ErrorAlert from './error-alert';
import { ServiceSupplierSignup } from '../../services/service-supplier-signup';
import { logEvent } from '../../services/eventsService';
import BtButton from '../bt-button/bt-button';
import Field from '../../components/shared/Field/Field';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import Spinner from '../../components/shared/Spinner/Spinner';

const ORG_NUMBER_REGEX = /^\d{6}-\d{4}$/;

const formatOrgNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  return digits.length > 6 ? digits.slice(0, 6) + '-' + digits.slice(6) : digits;
};

interface ExistingAdmin {
  name: string;
  email: string;
}

const StepCompany: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const history = useHistory();
  const { state, updateState, completeStep, setCurrentStep } = useSignupWizard();
  const abortRef = useRef<AbortController | null>(null);

  const [organizationNumber, setOrganizationNumber] = useState(state.organizationNumber || '');
  const [companyName, setCompanyName] = useState(state.companyName || '');
  const [orgNumberError, setOrgNumberError] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [synaFailed, setSynaFailed] = useState(false);
  const [orgExists, setOrgExists] = useState(false);
  const [existingAdmins, setExistingAdmins] = useState<ExistingAdmin[]>([]);
  const [termsAccepted, setTermsAccepted] = useState(!!state.termsAcceptedAt);

  const resetLookupState = () => {
    setOrgExists(false);
    setExistingAdmins([]);
    setSynaFailed(false);
    setCompanyName('');
    setOrgNumberError('');
  };

  const lookupOrg = async (orgNum: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    resetLookupState();
    setLoadingOrg(true);

    try {
      const response = await ServiceSupplierSignup.checkOrg(orgNum);
      if (controller.signal.aborted) return;

      if (response.exists) {
        setOrgExists(true);
        setExistingAdmins(response.admins || []);
        return;
      }

      if (response.companyName === null) {
        setSynaFailed(true);
      } else {
        setCompanyName(response.companyName);
      }

      updateState({
        address: response.address || '',
        city: response.city || '',
        postalCode: response.postalCode || '',
      });
    } catch {
      if (!controller.signal.aborted) {
        setOrgNumberError(t('company.lookupError', 'Kunde inte slå upp organisationsnumret. Försök igen.'));
      }
    } finally {
      if (!controller.signal.aborted) setLoadingOrg(false);
    }
  };

  const orgFormatError = t('company.orgNumberFormat', 'Organisationsnumret måste ha formatet XXXXXX-XXXX');

  const handleOrgNumberChange = (value: string) => {
    const formatted = formatOrgNumber(value);
    if (formatted !== organizationNumber) {
      resetLookupState();
      if (formatted.length === 11 && ORG_NUMBER_REGEX.test(formatted)) {
        lookupOrg(formatted);
      }
    }
    setOrganizationNumber(formatted);

  };

  const handleOrgNumberBlur = () => {
    if (organizationNumber && !ORG_NUMBER_REGEX.test(organizationNumber)) {
      setOrgNumberError(orgFormatError);
    }
  };

  const validate = (): boolean => {
    let valid = true;

    if (!ORG_NUMBER_REGEX.test(organizationNumber)) {
      setOrgNumberError(orgFormatError);
      valid = false;
    } else {
      setOrgNumberError('');
    }

    if (!companyName.trim()) {
      setCompanyNameError(t('company.companyNameRequired', 'Företagsnamn krävs'));
      valid = false;
    } else {
      setCompanyNameError('');
    }

    return valid;
  };

  const handleNext = () => {
    if (!validate() || !termsAccepted || orgExists) return;

    updateState({
      organizationNumber,
      companyName: companyName.trim(),
      termsAcceptedAt: state.termsAcceptedAt || new Date().toISOString(),
    });

    completeStep(1);
    logEvent({ event: 'signup_wizard_step_company', metadata: { sessionId: state.sessionId } });
    setCurrentStep(2);
    history.push('/supplier-sign-up/categories');
  };

  const canProceed =
    ORG_NUMBER_REGEX.test(organizationNumber) &&
    companyName.trim().length > 0 &&
    termsAccepted &&
    !orgExists &&
    !loadingOrg;

  return (
    <WizardLayout showProgressDots={false}>
      <div className="flex flex-col gap-6">
        <StepHeader title={t('company.title', 'Skapa ert kostnadsfria leverantörskonto')} stepNumber={1} description={t('company.description', 'Företagsuppgifter')} />

        <div className="relative">
          <Field
            id="org-number"
            value={organizationNumber}
            onChange={handleOrgNumberChange}
            onBlur={handleOrgNumberBlur}
            placeholder="XXXXXX-XXXX"
            label={t('company.orgNumber', 'Organisationsnummer')}
            required
            errorMessage={orgNumberError}
          />
          {loadingOrg && (
            <div style={{ position: 'absolute', right: '0.75rem', top: '38px' }}>
              <Spinner progress={0} size="xxs" />
            </div>
          )}
        </div>

        {orgExists && (
          <ErrorAlert title={t('company.orgExists', 'Det här företaget har redan ett konto i Accurator')}>
            {existingAdmins.length > 0 && (
              <>
                <p style={{ fontSize: '0.875rem', color: 'var(--red-700)', marginBottom: '0.25rem' }}>
                  {t('company.adminsLabel', 'Följande administratörer är kopplade till kontot:')}
                </p>
                <ul style={{ listStyleType: 'disc', listStylePosition: 'inside', fontSize: '0.875rem', color: 'var(--red-700)' }}>
                  {existingAdmins.map((admin, i) => (
                    <li key={i}>{admin.email}</li>
                  ))}
                </ul>
              </>
            )}
            <p style={{ fontSize: '0.875rem', color: 'var(--red-700)', marginTop: '0.75rem' }}>
              {t('company.takeoverInfo', 'Vill du ta över denna organisation, men saknar kontakt med nuvarande administratörer? Kontakta oss så hjälper vi dig vidare.')}
            </p>
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--red-700)' }}>
              <p style={{ fontWeight: 500 }}>{t('company.support', 'Support')}</p>
              <p><a href="mailto:support@accurator.se" style={{ textDecoration: 'underline', fontWeight: 500, color: 'var(--red-700)' }}>support@accurator.se</a></p>
              <p><a href="tel:+4608156225" style={{ textDecoration: 'underline', fontWeight: 500, color: 'var(--red-700)' }}>08 156-225</a></p>
            </div>
          </ErrorAlert>
        )}

        {synaFailed ? (
          <Field
            id="company-name"
            value={companyName}
            onChange={(val) => { setCompanyName(val); if (val.trim()) setCompanyNameError(''); }}
            placeholder={t('company.companyNamePlaceholder', 'Ange företagsnamn')}
            label={t('company.companyName', 'Företagsnamn')}
            required
            errorMessage={companyNameError}
          />
        ) : (
          <Field
            id="company-name"
            value={loadingOrg ? t('company.searchingCompany', 'Söker företag...') : companyName || t('company.companyNameAuto', 'Fylls i automatiskt från organisationsnummer')}
            label={t('company.companyName', 'Företagsnamn')}
            required
            disabled
            errorMessage={companyNameError}
          />
        )}

        <label className="flex items-center gap-1 cursor-pointer select-none">
          <StyledCheckbox
            checked={termsAccepted}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTermsAccepted(e.target.checked)}
          />
          <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)', letterSpacing: 'normal' }}>
            {t('company.termsPrefix', 'Jag godkänner Accurators')}{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--orange-500)', textDecoration: 'underline', fontWeight: 500 }}>
              {t('company.termsLink', 'användaravtal')}
            </a>
          </span>
        </label>

        <div className="flex justify-end">
          <BtButton onClick={handleNext} disabled={!canProceed}>{t('common.next', 'Nästa')} &rarr;</BtButton>
        </div>

        <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--gray-500)' }}>
          {t('company.hasAccount', 'Har du redan ett konto?')}{' '}
          <a href="/" style={{ color: 'var(--orange-500)', fontWeight: 500, textDecoration: 'underline' }}>{t('company.login', 'Logga in')}</a>
        </p>
      </div>
    </WizardLayout>
  );
};

export default StepCompany;
